'use client';

import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Headphones, Sparkles } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { getNavigationItems } from '@/contexts/application/config/navigation';
import type { NavigationCategory } from '@/shared/types/navigation';

interface DashboardGridProps {
  /** The signed-in person's own name. Empty when the identity carries none — the
   *  greeting then drops the comma instead of rendering "Welcome, !"
   *  (the family name it used to greet is no longer in the session claim). */
  name?: string;
  /** False until the family's first story exists — then the page is the CTA alone. */
  hasStories?: boolean;
}

const categoryOrder = ['tool', 'family'] as const satisfies readonly NavigationCategory[];

const categoryLabels: Record<(typeof categoryOrder)[number], string> = {
  tool: 'navigation.tools',
  family: 'navigation.family',
};

export function DashboardGrid({ name, hasStories = true }: DashboardGridProps) {
  const locale = useLocale();
  const t = useTranslations();
  const items = getNavigationItems();

  const grouped = categoryOrder
    .map((category) => ({
      category,
      items: items.filter((item) => item.category === category),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div data-testid="dashboard-grid" className="space-y-8">
      <div className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold">
          {name ? t('dashboard.welcome', { name }) : t('dashboard.welcomePlain')}
        </h1>
        <p className="text-muted-foreground">
          {t('dashboard.subtitle')}
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/5 border border-primary/20 p-6 md:p-8 flex flex-col items-center text-center gap-4">
        <Sparkles className="h-10 w-10 text-primary" />
        <h2 className="text-xl md:text-2xl font-bold">
          {hasStories ? t('dashboard.cta.title') : t('dashboard.first.title')}
        </h2>
        <p className="text-muted-foreground max-w-md">
          {hasStories ? t('dashboard.cta.description') : t('dashboard.first.description')}
        </p>
        <Button asChild size="lg">
          <Link href={`/${locale}/create`}>
            {hasStories ? t('dashboard.cta.button') : t('dashboard.first.button')}
          </Link>
        </Button>
        {!hasStories && (
          /* The other door, for a parent not ready to make anything yet. */
          <Link
            href={`/${locale}/gallery`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            data-testid="dashboard-first-gallery"
          >
            <Headphones className="size-4" />
            {t('dashboard.first.listen')}
          </Link>
        )}
      </div>

      {hasStories && grouped.map(({ category, items: categoryItems }) => (
        <div key={category} className="space-y-3">
          <h2 className="text-lg font-semibold text-muted-foreground">
            {t(categoryLabels[category])}
          </h2>
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 md:grid-cols-3 gap-3">
            {categoryItems.map((item) => (
              <Link
                key={item.id}
                data-testid={`dashboard-item-${item.id}`}
                href={`/${locale}${item.href}`}
                className="group relative flex flex-col items-center gap-2 rounded-xl border bg-card p-4 text-center transition-colors hover:bg-accent hover:border-primary/30"
              >
                <item.icon className="h-8 w-8" />
                <span className="font-medium text-sm">{t(item.name)}</span>
                <span className="text-xs text-muted-foreground line-clamp-2">
                  {t(item.description)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
